/**
 * Testimonial Slideshow Component
 * A lightweight carousel web component for testimonials.
 */

if (!customElements.get('testimonial-slideshow')) {
  class TestimonialSlideshow extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isTransitioning = false;
      this.autoplayTimer = null;
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.init();

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this._resizeObserver) this._resizeObserver.disconnect();

      if (window.Shopify?.designMode) {
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }
    }

    init() {
      this.wrapper = this.querySelector('.swiper-wrapper');
      this.slides = Array.from(this.querySelectorAll('.swiper-slide'));
      this.prevBtn = this.querySelector('.testimonial-slideshow__nav--prev');
      this.nextBtn = this.querySelector('.testimonial-slideshow__nav--next');
      this.slideCount = this.slides.length;

      if (this.slideCount <= 1) return;

      // Equalize wrapper height to tallest slide to prevent layout jumps
      this.equalizeHeight();
      this._resizeObserver = new ResizeObserver(() => this.equalizeHeight());
      this._resizeObserver.observe(this);

      // Set initial state
      this.slides.forEach((slide, i) => {
        slide.style.position = i === 0 ? 'relative' : 'absolute';
        slide.style.top = '0';
        slide.style.left = '0';
        slide.style.width = '100%';
        slide.style.opacity = i === 0 ? '1' : '0';
        slide.style.visibility = i === 0 ? 'visible' : 'hidden';
        slide.style.transition = 'opacity 0.5s ease, visibility 0.5s ease';
        slide.setAttribute('aria-hidden', i !== 0);
      });

      this.wrapper.style.position = 'relative';

      // Bind navigation
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.goTo(this.currentIndex - 1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.goTo(this.currentIndex + 1));
      }

      // Touch/swipe support
      this.setupTouch();

      // Autoplay
      const speed = parseInt(this.getAttribute('data-autoplay-speed') || '0', 10);
      if (speed > 0) {
        this.autoplayDelay = speed * 1000;
        this.startAutoplay();

        // Pause on hover
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
      }
    }

    goTo(index) {
      if (this.isTransitioning || this.slideCount <= 1) return;

      this.isTransitioning = true;

      const prevIndex = this.currentIndex;
      // Wrap around
      if (index < 0) index = this.slideCount - 1;
      if (index >= this.slideCount) index = 0;

      this.currentIndex = index;

      // Fade out previous slide
      const prevSlide = this.slides[prevIndex];
      const nextSlide = this.slides[index];

      // Bring next slide to front but keep invisible, then fade in
      nextSlide.style.visibility = 'visible';
      nextSlide.style.opacity = '0';
      nextSlide.style.position = 'absolute';

      // Force reflow
      nextSlide.offsetHeight;

      nextSlide.style.opacity = '1';
      nextSlide.setAttribute('aria-hidden', 'false');

      prevSlide.style.opacity = '0';
      prevSlide.setAttribute('aria-hidden', 'true');

      // After transition
      setTimeout(() => {
        prevSlide.style.visibility = 'hidden';
        prevSlide.style.position = 'absolute';
        nextSlide.style.position = 'relative';
        this.isTransitioning = false;
      }, 500);

      // Reset autoplay timer
      if (this.autoplayDelay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    setupTouch() {
      let startX = 0;
      let startY = 0;

      this.addEventListener(
        'touchstart',
        (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
        },
        { passive: true }
      );

      this.addEventListener(
        'touchend',
        (e) => {
          const diffX = e.changedTouches[0].clientX - startX;
          const diffY = e.changedTouches[0].clientY - startY;

          // Only respond to horizontal swipes
          if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 40) {
            if (diffX < 0) {
              this.goTo(this.currentIndex + 1);
            } else {
              this.goTo(this.currentIndex - 1);
            }
          }
        },
        { passive: true }
      );
    }

    startAutoplay() {
      if (!this.autoplayDelay || this.autoplayTimer) return;
      this.autoplayTimer = setInterval(() => {
        this.goTo(this.currentIndex + 1);
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    equalizeHeight() {
      // Temporarily make all slides static so we can measure their natural heights
      const prevStyles = this.slides.map((slide) => ({
        position: slide.style.position,
        opacity: slide.style.opacity,
        visibility: slide.style.visibility,
      }));

      this.slides.forEach((slide) => {
        slide.style.position = 'relative';
        slide.style.opacity = '0';
        slide.style.visibility = 'hidden';
      });

      const maxHeight = Math.max(...this.slides.map((slide) => slide.offsetHeight));

      // Restore styles
      this.slides.forEach((slide, i) => {
        slide.style.position = prevStyles[i].position || '';
        slide.style.opacity = prevStyles[i].opacity || '';
        slide.style.visibility = prevStyles[i].visibility || '';
      });

      if (maxHeight > 0) {
        this.wrapper.style.minHeight = maxHeight + 'px';
      }
    }

    handleBlockSelect(e) {
      if (e.detail.sectionId !== this.sectionId) return;

      const slideIndex = parseInt(e.target.dataset?.swiperSlideIndex, 10);
      if (!isNaN(slideIndex)) {
        this.stopAutoplay();
        this.goTo(slideIndex);
      }
    }

    handleBlockDeselect(e) {
      if (e.detail.sectionId !== this.sectionId) return;
      if (this.autoplayDelay) this.startAutoplay();
    }

    get sectionId() {
      return this.getAttribute('data-section-id');
    }
  }

  customElements.define('testimonial-slideshow', TestimonialSlideshow);
}
